import type { IconSet, LabelSet, ThemeOptions, UploadSourceOption } from '../types';

export const DEFAULT_BASE_URL = 'https://upload.autorender.io/api/v1';

export const DEFAULT_ASSET_DELIVERY_BASE_URL = 'https://assets.autorender.io';


export const DEFAULT_LABELS: Required<LabelSet> = {
  title: 'Upload files',
  description: 'Drag & drop files or click to browse.',
  selectButton: 'Upload files',
  selectFolderButton: 'Upload folder',
  uploadButton: 'Upload',
  uploading: 'Uploading...',
  uploadComplete: 'All files uploaded successfully.',
  uploadFailed: 'Unable to upload files. Please try again.',
  retry: 'Retry',
  remove: 'Remove',
  addMore: 'Add more',
  done: 'Done',
  clear: 'Clear',
  cancel: 'Cancel',
  dropzoneTitle: 'Drop files here',
  duplicateFileExists: 'The following file already exists in this folder:',
  duplicateFilesExist: 'The following files already exist in this folder:',
  duplicateFileError: 'File already exists in this folder.',
  invalidApiKey: 'Invalid API key. Please verify your credentials and try again.',
};

export const DEFAULT_ICONS: Required<IconSet> = {
  upload: `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 16a1 1 0 0 1-1-1V7.41l-2.3 2.3a1 1 0 1 1-1.4-1.42l4-4a1 1 0 0 1 1.4 0l4 4a1 1 0 1 1-1.4 1.42L13 7.41V15a1 1 0 0 1-1 1Z" />
      <path d="M5 20a3 3 0 0 1-3-3v-1a1 1 0 0 1 2 0v1a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1a1 1 0 1 1 2 0v1a3 3 0 0 1-3 3Z" />
    </svg>
  `,
  success: `
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
      <path d="M9.5 17a1 1 0 0 1-.7-.29l-3.5-3.5a1 1 0 1 1 1.4-1.42l2.8 2.79 7.1-7.09a1 1 0 0 1 1.4 1.42l-7.8 7.79a1 1 0 0 1-.7.3Z" fill="currentColor" />
    </svg>
  `,
  error: `
    <svg viewBox="0 0 24 24" aria-hidden="true" >
      <path d="m12 13.41 3.29 3.3a1 1 0 0 0 1.42-1.42L13.41 12l3.3-3.29a1 1 0 0 0-1.42-1.42L12 10.59l-3.29-3.3a1 1 0 1 0-1.42 1.42L10.59 12l-3.3 3.29a1 1 0 0 0 1.42 1.42L12 13.41Z" />
    </svg>
  `,
  remove: `
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M9 5.25C9 4.55964 9.55964 4 10.25 4H13.75C14.4404 4 15 4.55964 15 5.25V7H9V5.25Z"
        stroke="currentColor"
        stroke-width="1.5"
      />
      <path
        d="M5 7H19"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
      />
      <rect
        x="6.5"
        y="7"
        width="11"
        height="12.5"
        rx="2"
        stroke="currentColor"
        stroke-width="1.5"
      />
      <path
        d="M10 11.5V16"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
      />
      <path
        d="M14 11.5V16"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
      />
    </svg>
  `,
  close: `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13.41 12 18 7.41 16.59 6 12 10.59 7.41 6 6 7.41 10.59 12 6 16.59 7.41 18 12 13.41 16.59 18 18 16.59 13.41 12Z" />
    </svg>
  `,
};

export const DEFAULT_SOURCES: UploadSourceOption[] = [
  {
    id: 'device',
    label: 'From device',
    kind: 'device',
    icon: `
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
        <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" stroke-width="1.5" />
        <path d="M9.5 4h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
    `,
  },
  {
    id: 'camera',
    label: 'Camera',
    kind: 'camera',
    icon: `
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
        <path d="M5 7h2l1-2h8l1 2h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
        <circle cx="12" cy="13" r="3.25" stroke="currentColor" stroke-width="1.5" />
      </svg>
    `,
  },
  {
    id: 'facebook',
    label: 'Facebook',
    kind: 'facebook',
    icon: `
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5" />
        <path d="M13 8h2V6h-2a3 3 0 0 0-3 3v1H8v2h2v6h2v-6h2v-2h-2V9a1 1 0 0 1 1-1Z" fill="currentColor" />
      </svg>
    `,
  },
  {
    id: 'google-drive',
    label: 'Google Drive',
    kind: 'google-drive',
    icon: `
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
        <path d="M6.5 18.5h11l3-5-5-8h-6l-5 8 2 3" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
        <path d="M8 13.5h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
    `,
  },
];

export const DEFAULT_THEME: Required<
  Pick<
    ThemeOptions,
    'appearance' | 'accentColor' | 'borderRadius' | 'dropzoneBackground' | 'dropzoneBorder'
  >
> = {
  appearance: 'light',
  accentColor: 'var(--ar-color-accent)',
  borderRadius: 8,
  dropzoneBackground: 'var(--ar-color-muted)',
  dropzoneBorder: 'var(--ar-color-border)',
};
